import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ApartmentOutlined as CustomerIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CreateNewFolderOutlined as AddProjectIcon,
} from '@mui/icons-material';
import PageHeader, { PageContainer } from '../components/PageHeader';
import MultiSelectFilter, { SearchFilter, segmentedSx } from '../components/monthly/MultiSelectFilter';
import { useFillViewportHeight } from '../components/monthly/tableStyles';
import NewProjectPanel from '../components/projects/NewProjectPanel';
import StatusChip from '../components/projects/StatusChip';
import { projectsBase, saveProjectsNav } from '../components/projects/projectsNav';
import { GREEN, INK, LINE, MUTED } from '../components/home/homeTheme';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { fetchProjectLookups, fetchProjectsList, fmtPeriod } from '../utils/projectsService';

const PAGE_SIZE = 100;
const EMPTY = [];
const num = (n) => Number(n || 0).toLocaleString('en-GB');
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const headSx = { fontSize: 12, fontWeight: 700, color: MUTED, bgcolor: 'var(--rt-paper)', py: 1, borderBottom: `1px solid ${LINE}`, whiteSpace: 'nowrap' };
const cellSx = { fontSize: 13, color: INK, py: 0.9, borderBottom: `1px solid ${LINE}` };
const listParam = (v) => (v ? v.split(',').filter(Boolean) : EMPTY);

/**
 * Projects V2: elenco con filtri a pillola, righe cliccabili e pannello di creazione.
 * Filtri e ordinamento sono nell'URL, così tornando dal dettaglio l'elenco è come lo si era lasciato.
 */
export default function ProjectsV2() {
  const navigate = useNavigate();
  const location = useLocation();
  const base = projectsBase(location.pathname);
  const [params, setParams] = useSearchParams();
  const { canManageProjects, isServiceManager, isTeamLeader, isJRServiceManager } = useRoleAccess();
  const { userId } = useCurrentUser();
  const ownOnly = isServiceManager || isTeamLeader || isJRServiceManager;

  const [data, setData] = useState({ projects: EMPTY, customers: EMPTY, serviceTypes: EMPTY });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Messaggio arrivato dal dettaglio (es. "Project deleted"): si mostra una volta sola
  const [snack, setSnack] = useState(location.state?.snack || null);
  useEffect(() => {
    if (location.state?.snack) navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ownOnly && !userId) return undefined;
    let cancelled = false;
    setError(null);
    Promise.all([fetchProjectsList({ ownOnly, userId }), fetchProjectLookups()])
      .then(([projects, lookups]) => !cancelled && setData({ projects, ...lookups }))
      .catch((e) => !cancelled && setError(`Unable to load projects: ${e.message}`))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [ownOnly, userId]);

  const { projects, customers, serviceTypes } = data;
  const customerName = useMemo(() => Object.fromEntries(customers.map((c) => [String(c.id), c.name])), [customers]);
  const customerOf = (p) => p.customer?.name || customerName[String(p.customer_id)] || '';
  // Finestra di inserimento dei worklog (giorni prima e dopo oggi) e blocco del mese precedente
  const daysBefore = (p) => p.days_before_block || 1;
  const daysAfter = (p) => p.days_after_block || 1;

  // ---- Filtri (nell'URL) ----
  const search = params.get('q') || '';
  const status = params.get('status') || 'active';
  const customerParam = params.get('customer') || '';
  const customerFilter = useMemo(() => listParam(customerParam), [customerParam]);
  const sortKey = params.get('sort') || 'name';
  const sortDir = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const setParam = (key, value) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === '' || value == null || (Array.isArray(value) && !value.length)) next.delete(key);
        else next.set(key, Array.isArray(value) ? value.join(',') : value);
        return next;
      },
      { replace: true },
    );
  const setCreating = (on) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (on) next.set('new', '1');
      else next.delete('new');
      return next;
    });

  const counts = useMemo(() => ({ active: projects.filter((p) => p.active).length, inactive: projects.filter((p) => !p.active).length }), [projects]);
  const customerOptions = useMemo(() => {
    const used = new Set(projects.map((p) => String(p.customer_id ?? p.customer?.id)));
    return customers.filter((c) => used.has(String(c.id))).map((c) => ({ id: String(c.id), name: c.name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [projects, customers]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = projects.filter((p) => {
      if (status === 'active' && !p.active) return false;
      if (status === 'inactive' && p.active) return false;
      if (customerFilter.length && !customerFilter.includes(String(p.customer_id ?? p.customer?.id))) return false;
      if (!q) return true;
      return [p.name, p.project_code, customerOf(p), p.description, p.ticket_id_prefix, String(p.id)].some((v) => String(v || '').toLowerCase().includes(q));
    });
    const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''));
    const sorters = {
      name: byName,
      customer: (a, b) => customerOf(a).localeCompare(customerOf(b)),
      window: (a, b) => daysBefore(a) + daysAfter(a) - (daysBefore(b) + daysAfter(b)) || Number(!!a.previous_month_blocked) - Number(!!b.previous_month_blocked),
      period: (a, b) => String(a.start_date || '').localeCompare(String(b.start_date || '')),
      status: (a, b) => Number(!!b.active) - Number(!!a.active),
      updated: (a, b) => String(a.updated_at || '').localeCompare(String(b.updated_at || '')),
    };
    const sorter = sorters[sortKey] || byName;
    list.sort((a, b) => sorter(a, b) || byName(a, b));
    if (sortDir === 'desc') list.reverse();
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, search, status, customerFilter, sortKey, sortDir, customerName]);

  // Ordine e filtri attuali: servono al dettaglio per le frecce e per tornare qui
  const navSearch = useMemo(() => {
    const next = new URLSearchParams(location.search);
    next.delete('new');
    const s = next.toString();
    return s ? `?${s}` : '';
  }, [location.search]);
  useEffect(() => {
    if (!loading) saveProjectsNav({ ids: rows.map((p) => String(p.id)), search: navSearch });
  }, [rows, loading, navSearch]);

  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [rows.length, sortKey, sortDir]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const creating = params.get('new') === '1';
  const tableRef = useRef(null);
  const fillHeight = useFillViewportHeight(tableRef, [loading, error]);

  const sortHeader = (key, label, align) => (
    <TableCell sx={{ ...headSx, textAlign: align }} sortDirection={sortKey === key ? sortDir : false}>
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? sortDir : 'asc'}
        onClick={() =>
          setParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set('sort', key);
              next.set('dir', sortKey === key && sortDir === 'asc' ? 'desc' : 'asc');
              return next;
            },
            { replace: true },
          )
        }
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <PageContainer>
      <PageHeader title="Projects" subtitle={loading ? 'Loading…' : `${num(projects.length)} projects · ${num(counts.active)} active`}>
        {canManageProjects() && (
          <Button
            variant="contained"
            color="success"
            startIcon={<AddProjectIcon />}
            onClick={() => setCreating(true)}
            disabled={loading}
            sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', height: 40, bgcolor: GREEN }}
          >
            Add project
          </Button>
        )}
      </PageHeader>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Filtri */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <SearchFilter value={search} onChange={(v) => setParam('q', v)} placeholder="Search name, code, customer, ticket prefix" width={320} />
          <ToggleButtonGroup exclusive size="small" value={status} onChange={(e, v) => v && setParam('status', v === 'active' ? '' : v)} sx={segmentedSx}>
            <ToggleButton value="active">Active ({num(counts.active)})</ToggleButton>
            <ToggleButton value="inactive">Inactive ({num(counts.inactive)})</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
          <MultiSelectFilter label="Customers" allLabel="All customers" icon={<CustomerIcon />} options={customerOptions} value={customerFilter} onChange={(v) => setParam('customer', v)} width={240} />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box ref={tableRef} sx={{ border: `1px solid ${LINE}`, borderRadius: 3, bgcolor: 'var(--rt-paper)', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress color="success" />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: fillHeight ? fillHeight - 48 : 'calc(100vh - 260px)' }}>
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 1040 }}>
                  <colgroup>
                    <col />
                    <col style={{ width: 210 }} />
                    <col style={{ width: 180 }} />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 220 }} />
                    <col style={{ width: 96 }} />
                    <col style={{ width: 120 }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      {sortHeader('name', 'Project')}
                      {sortHeader('customer', 'Customer')}
                      {sortHeader('window', 'Worklog restrictions')}
                      <TableCell sx={headSx}>Ticket prefix</TableCell>
                      {sortHeader('period', 'Period')}
                      {sortHeader('status', 'Status')}
                      {sortHeader('updated', 'Last updated', 'right')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageRows.map((p) => (
                      <TableRow key={p.id} hover onClick={() => navigate(`${base}/${p.id}`)} sx={{ cursor: 'pointer' }}>
                        <TableCell sx={cellSx}>
                          <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: p.active ? INK : MUTED }} title={p.name}>
                            {p.name}
                          </Typography>
                          <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
                            {p.project_code || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <Typography noWrap sx={{ fontSize: 13 }} title={customerOf(p)}>
                            {customerOf(p) || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <Typography noWrap sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                            {daysBefore(p)}d before · {daysAfter(p)}d after
                          </Typography>
                          <Typography noWrap sx={{ fontSize: 12, fontWeight: p.previous_month_blocked ? 700 : 400, color: p.previous_month_blocked ? 'var(--rt-amber)' : MUTED }}>
                            {p.previous_month_blocked ? 'Previous month blocked' : 'Previous month open'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...cellSx, fontFamily: 'monospace', fontSize: 12.5, color: p.ticket_id_prefix ? INK : MUTED }}>{p.ticket_id_prefix || '—'}</TableCell>
                        <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtPeriod(p)}</TableCell>
                        <TableCell sx={cellSx}>
                          <StatusChip active={!!p.active} />
                        </TableCell>
                        <TableCell sx={{ ...cellSx, textAlign: 'right', color: MUTED, whiteSpace: 'nowrap' }}>{fmtDate(p.updated_at)}</TableCell>
                      </TableRow>
                    ))}
                    {!pageRows.length && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ ...cellSx, py: 4, textAlign: 'center', color: MUTED }}>
                          No projects match these filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderTop: `1px solid ${LINE}`, bgcolor: 'var(--rt-subtle-2)' }}>
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  {num(rows.length)} project{rows.length === 1 ? '' : 's'}
                  {rows.length !== projects.length ? ` of ${num(projects.length)}` : ''} · click a row to open it
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 13, color: MUTED }}>
                    {rows.length ? `${num(page * PAGE_SIZE + 1)}–${num(Math.min(rows.length, (page + 1) * PAGE_SIZE))} of ${num(rows.length)}` : '0 of 0'}
                  </Typography>
                  <IconButton size="small" disabled={page === 0} onClick={() => setPage((n) => n - 1)} aria-label="Previous page">
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" disabled={page >= pageCount - 1} onClick={() => setPage((n) => n + 1)} aria-label="Next page">
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <NewProjectPanel
        open={creating && canManageProjects()}
        onClose={() => setCreating(false)}
        onCreated={(id) => (id ? navigate(`${base}/${id}`, { state: { snack: 'Project created' } }) : setCreating(false))}
        customers={customers}
        serviceTypes={serviceTypes}
        userId={userId}
      />

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />

    </PageContainer>
  );
}
